import {
  BaseService,
  type BaseServiceContext,
  throwAppError,
} from '@elasticnote/base-utils';
import { SERVICE_NAME } from '@elasticnote/constants';
import {
  createDefaultElasticnoteProjectConfig,
  DEFAULT_ELASTICNOTE_MAIN_TEX,
  exportProjectZip,
  exportProjectZipStream,
  importProjectZip,
  importProjectZipBlob,
  isElasticnoteProjectConfig,
  projectFilePath,
  type ProjectFilePath,
  type ProjectStorageProvider,
  type ElasticnoteProjectConfig,
} from '@elasticnote/project-storage';
import type { WorkspaceInfo } from '@elasticnote/types';
import {
  canonicalProfileForMode,
  detectProjectTargets,
  isProjectProfile,
  projectModeById,
  projectModeForProfile,
  type ProjectProfile,
  type ProjectTarget,
} from '@elasticnote/project-runtime';
import type { WorkspaceOpsService } from './workspace-ops-service';


const encoder = new TextEncoder();
const defaultMainTexBytes = encoder.encode(DEFAULT_ELASTICNOTE_MAIN_TEX);

function bytesEqual(left: Uint8Array | undefined, right: Uint8Array): boolean {
  if (!left || left.byteLength !== right.byteLength) return false;
  for (let index = 0; index < right.byteLength; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

async function isPristineBootstrapProject(
  provider: ProjectStorageProvider,
  signal?: AbortSignal,
): Promise<boolean> {
  const entries = await provider.listEntries(signal);
  const files = entries.filter((entry) => entry.kind === 'file');
  if (files.length !== 1 || String(files[0]?.path) !== 'main.tex') return false;
  const mainTex = await provider.readFile(projectFilePath('main.tex'), signal);
  return bytesEqual(mainTex, defaultMainTexBytes);
}

async function importReplacingPristineBootstrap<T>(
  provider: ProjectStorageProvider,
  importArchive: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await importArchive();
  } catch (initialError) {
    const collidesWithBootstrapMain =
      initialError instanceof Error &&
      /existing project file: main\.tex/u.test(initialError.message);
    if (!collidesWithBootstrapMain || !(await isPristineBootstrapProject(provider, signal))) {
      throw initialError;
    }
  }

  // Retry only the precise whole-project portability case: the incoming
  // archive contains main.tex and the target contains Elasticnote's untouched
  // generated main.tex. Partial archives that do not collide never remove it.
  await provider.deleteFile(projectFilePath('main.tex'), signal);
  try {
    return await importArchive();
  } catch (error) {
    await provider.writeFile(projectFilePath('main.tex'), defaultMainTexBytes, signal);
    throw error;
  }
}

export type ProjectStorageProviderFactory = (
  workspace: WorkspaceInfo,
) => Promise<ProjectStorageProvider>;

export type ProjectStorageMutationEvent = (
  | { wsName: string; type: 'write'; path: string; size?: number }
  | { wsName: string; type: 'create-directory'; path: string }
  | { wsName: string; type: 'delete-file'; path: string }
  | { wsName: string; type: 'delete-directory'; path: string }
  | { wsName: string; type: 'move-file'; from: string; to: string }
) & {
  /** Provider-root journal was durably recorded before local mutation. */
  prepared?: boolean;
  /** SHA-256 readback proof for an attempted write or move (browser-private journal only). */
  expectedHash?: string;
  /** Previous file hash for a delete, proving the target existed at preparation time. */
  previousHash?: string;
  /** A directory was present at preparation; absent folders cannot prove deletion. */
  observedBefore?: boolean;
};

type Config = {
  createProvider: ProjectStorageProviderFactory;
  onMutation?: (event: ProjectStorageMutationEvent) => void;
  /** Awaited before OPFS mutation: rejecting this promise prevents the mutation. */
  onBeforeMutation?: (event: ProjectStorageMutationEvent) => Promise<string | undefined>;
  /** Completes the local write-ahead intent, not the remote provider write. */
  onMutationCommitted?: (event: ProjectStorageMutationEvent, reservation: string) => Promise<void>;
};

/**
 * Owns the storage-neutral Elasticnote project boundary. Editor, ZIP, Git, compiler,
 * assets, and AI should consume this service rather than opening OPFS/native
 * handles independently.
 */
class NotifyingProjectStorage implements ProjectStorageProvider {
  constructor(
    private readonly wsName: string,
    private readonly delegate: ProjectStorageProvider,
    private readonly notify: (event: ProjectStorageMutationEvent) => void,
    private readonly prepare?: (event: ProjectStorageMutationEvent) => Promise<string | undefined>,
    private readonly commit?: (event: ProjectStorageMutationEvent, reservation: string) => Promise<void>,
  ) {}

  /** Hash existing bytes only for uncommon destructive operations. Do not scan on autosave. */
  private async fingerprint(path: ProjectFilePath): Promise<string | undefined> {
    try {
      const bytes = await this.delegate.readFile(path);
      return bytes ? this.digest(bytes) : undefined;
    } catch {
      // An unavailable preimage cannot be used as proof during recovery.
      return undefined;
    }
  }

  private async digest(bytes: Uint8Array): Promise<string> {
    const hash = await crypto.subtle.digest('SHA-256', bytes.slice().buffer);
    return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  private async before(event: ProjectStorageMutationEvent): Promise<string | undefined> {
    // A failed durable journal MUST prevent the underlying storage mutation.
    return this.prepare?.(event);
  }

  private async after(event: ProjectStorageMutationEvent, reservation?: string): Promise<void> {
    if (reservation) {
      if (!this.commit) throw new Error('A prepared local mutation cannot be committed without its journal owner.');
      await this.commit(event, reservation);
    }
  }

  get capabilities() { return this.delegate.capabilities; }
  listEntries(signal?: AbortSignal) { return this.delegate.listEntries(signal); }
  readFile(path: ProjectFilePath, signal?: AbortSignal) { return this.delegate.readFile(path, signal); }
  async writeFile(path: ProjectFilePath, data: Uint8Array, signal?: AbortSignal) {
    const event = { wsName: this.wsName, type: 'write', path: String(path), size: data.byteLength } as const;
    const reservation = await this.before(this.prepare ? { ...event, expectedHash: await this.digest(data) } : event);
    await this.delegate.writeFile(path, data, signal);
    await this.after(event, reservation);
    this.notify({ ...event, prepared: Boolean(reservation) });
  }
  async createDirectory(path: import('@elasticnote/project-storage').ProjectDirectoryPath, signal?: AbortSignal) {
    const event = { wsName: this.wsName, type: 'create-directory', path: String(path) } as const;
    const reservation = await this.before(event);
    await this.delegate.createDirectory(path, signal);
    await this.after(event, reservation);
    this.notify({ ...event, prepared: Boolean(reservation) });
  }
  async deleteFile(path: ProjectFilePath, signal?: AbortSignal) {
    const event = { wsName: this.wsName, type: 'delete-file', path: String(path) } as const;
    const reservation = await this.before(this.prepare ? { ...event, previousHash: await this.fingerprint(path) } : event);
    await this.delegate.deleteFile(path, signal);
    await this.after(event, reservation);
    this.notify({ ...event, prepared: Boolean(reservation) });
  }
  async deleteDirectory(path: import('@elasticnote/project-storage').ProjectDirectoryPath, signal?: AbortSignal) {
    const event = { wsName: this.wsName, type: 'delete-directory', path: String(path) } as const;
    let observedBefore: boolean | undefined;
    if (this.prepare) {
      try { observedBefore = (await this.delegate.listEntries(signal)).some((entry) => entry.kind === 'directory' && String(entry.path) === String(path)); }
      catch { observedBefore = undefined; }
    }
    const reservation = await this.before(this.prepare ? { ...event, observedBefore } : event);
    await this.delegate.deleteDirectory(path, signal);
    await this.after(event, reservation);
    this.notify({ ...event, prepared: Boolean(reservation) });
  }
  async moveFile(from: ProjectFilePath, to: ProjectFilePath, signal?: AbortSignal) {
    const event = { wsName: this.wsName, type: 'move-file', from: String(from), to: String(to) } as const;
    const reservation = await this.before(this.prepare ? { ...event, expectedHash: await this.fingerprint(from) } : event);
    await this.delegate.moveFile(from, to, signal);
    await this.after(event, reservation);
    this.notify({ ...event, prepared: Boolean(reservation) });
  }
}

export class ProjectStorageService extends BaseService {
  static deps = ['workspaceOps'] as const;

  private readonly providers = new Map<string, ProjectStorageProvider>();

  constructor(
    context: BaseServiceContext,
    private readonly dependencies: { workspaceOps: WorkspaceOpsService },
    private readonly config: Config,
  ) {
    super(SERVICE_NAME.projectStorageService, context, dependencies);
  }

  async hookMount(): Promise<void> {
    const unsubscribe = this.store.sub(
      this.dependencies.workspaceOps.$workspaceInfoChange,
      () => this.providers.clear(),
    );
    this.addCleanup(unsubscribe);
    this.addCleanup(() => this.providers.clear());
  }

  async getProvider(wsName: string): Promise<ProjectStorageProvider> {
    await this.mountPromise;
    const cached = this.providers.get(wsName);
    if (cached) {
      return cached;
    }

    const workspace = await this.dependencies.workspaceOps.getWorkspaceInfo(wsName);
    if (!workspace) {
      throwAppError(
        'error::workspace:not-found',
        `Workspace not found: ${wsName}`,
        { wsName },
      );
    }

    const rawProvider = await this.config.createProvider(workspace);
    const provider = this.config.onMutation
      ? new NotifyingProjectStorage(wsName, rawProvider, this.config.onMutation, this.config.onBeforeMutation, this.config.onMutationCommitted)
      : rawProvider;
    this.providers.set(wsName, provider);
    return provider;
  }

  async getProjectConfig(wsName: string): Promise<ElasticnoteProjectConfig> {
    await this.mountPromise;
    const metadata = await this.dependencies.workspaceOps.getWorkspaceMetadata(wsName);
    return isElasticnoteProjectConfig(metadata.project)
      ? metadata.project
      : createDefaultElasticnoteProjectConfig();
  }


  async updateProjectConfig(
    wsName: string,
    update: (current: ElasticnoteProjectConfig) => ElasticnoteProjectConfig,
  ): Promise<ElasticnoteProjectConfig> {
    const current = await this.getProjectConfig(wsName);
    const next = update(current);
    if (!isElasticnoteProjectConfig(next)) {
      throw new Error('Invalid Elasticnote project configuration update');
    }
    await this.dependencies.workspaceOps.updateWorkspaceMetadata(wsName, (metadata) => ({
      ...metadata,
      project: next,
    }));
    return next;
  }


  async getProjectProfile(wsName: string): Promise<ProjectProfile | undefined> {
    await this.mountPromise;
    const metadata = await this.dependencies.workspaceOps.getWorkspaceMetadata(wsName);
    if (isProjectProfile(metadata.projectProfile)) {
      const mode = projectModeForProfile(metadata.projectProfile);
      return mode && metadata.projectProfile.modeId !== mode.id ? { ...metadata.projectProfile, modeId: mode.id } : metadata.projectProfile;
    }
    const storedMode = projectModeById(typeof metadata.projectModeId === 'string' ? metadata.projectModeId : undefined);
    return storedMode ? canonicalProfileForMode(storedMode.id) : undefined;
  }

  async updateProjectProfile(wsName: string, next: ProjectProfile): Promise<ProjectProfile> {
    if (!isProjectProfile(next)) throw new Error('Invalid Elasticnote project profile');
    const mode = projectModeForProfile(next);
    const canonical = mode ? { ...next, modeId: mode.id, entry: next.entry || mode.defaultEntryFile } : next;
    await this.dependencies.workspaceOps.updateWorkspaceMetadata(wsName, (metadata) => {
      const updated: Record<string, unknown> = {
        ...metadata,
        projectProfile: canonical,
        ...(mode ? { projectModeId: mode.id } : {}),
      };
      if (mode?.id === 'latex') {
        if (!isElasticnoteProjectConfig(updated.project)) updated.project = createDefaultElasticnoteProjectConfig();
      } else if (mode) {
        delete updated.project;
      }
      return updated;
    });
    return canonical;
  }

  async detectProjectTargets(
    wsName: string,
    options: { activePath?: string; signal?: AbortSignal } = {},
  ): Promise<ProjectTarget[]> {
    const provider = await this.getProvider(wsName);
    const entries = await provider.listEntries(options.signal);
    const interesting = entries.filter((entry) => entry.kind === 'file' && (
      /(?:^|\/)(?:package\.json|pyproject\.toml|requirements\.txt|Cargo\.toml|go\.mod|pom\.xml|build\.gradle(?:\.kts)?|CMakeLists\.txt|Makefile|vite\.config\.(?:js|ts)|index\.html|README\.md|document\.md|main\.md|main\.(?:py|js|ts|c|cc|cpp|cxx|java|go|rs|php|rb|R|sh)|app\.py|src\/index\.(?:js|ts))$/u.test(String(entry.path))
      || /\.(?:tex|js|jsx|ts|tsx|py|c|cc|cpp|cxx|h|hpp|java|go|rs|php|rb|r|sh|bash|md|markdown)$/iu.test(String(entry.path))
      || String(entry.path) === '.elasticnote.yml'
    ) && entry.size <= 256 * 1024);
    const decoder = new TextDecoder('utf-8', { fatal: false });
    const files: Record<string, string> = {};
    for (const entry of interesting.slice(0, 256)) {
      const bytes = await provider.readFile(entry.path, options.signal);
      if (bytes) files[String(entry.path)] = decoder.decode(bytes);
    }
    const selectedProfile = await this.getProjectProfile(wsName);
    const portableConfigText = files['.elasticnote.yml'];
    return detectProjectTargets({ files, selectedProfile, portableConfigText, activePath: options.activePath });
  }

  async exportZip(
    wsName: string,
    options: { includeGit?: boolean; signal?: AbortSignal } = {},
  ): Promise<Uint8Array> {
    const provider = await this.getProvider(wsName);
    return exportProjectZip(provider, options);
  }

  async exportZipStream(
    wsName: string,
    options: { includeGit?: boolean; signal?: AbortSignal } = {},
  ): Promise<ReadableStream<Uint8Array>> {
    const provider = await this.getProvider(wsName);
    return exportProjectZipStream(provider, options);
  }

  async importZip(
    wsName: string,
    archive: Uint8Array,
    options: { signal?: AbortSignal } = {},
  ): Promise<ProjectFilePath[]> {
    const provider = await this.getProvider(wsName);
    return importReplacingPristineBootstrap(
      provider,
      () => importProjectZip(archive, provider, options),
      options.signal,
    );
  }

  async importZipBlob(
    wsName: string,
    archive: Blob,
    options: { signal?: AbortSignal } = {},
  ): Promise<ProjectFilePath[]> {
    const provider = await this.getProvider(wsName);
    return importReplacingPristineBootstrap(
      provider,
      () => importProjectZipBlob(archive, provider, options),
      options.signal,
    );
  }
}
